import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageEditor from 'tui-image-editor';
import PropTypes from 'prop-types';
import 'tui-image-editor/dist/tui-image-editor.css';
import * as Sentry from '@sentry/browser';

import { putObjectPreSignedUrl, uploadFileToStorage } from 'src/utils/helpers';
import Button from 'src/components/Button';
import client from 'src/utils/api/client';
import { GET_ATTACHMENT_ID } from 'src/graphql/attachment/query';
import { JOB_MOBILE_PHOTO_ENTITY_NAME } from 'src/components/JobMobileDataTab/constants';
import { notification } from 'src/components/Notification';
import Loader from 'src/components/Loader';
import moment from 'moment';
import s from './ImageEditing.styles';
import 'src/styles/ImageEditor.css';

const BLUR = 0.5;

const ImageEditing = (props) => {

  const {
    getImagePath,
    setEnablePhotoEditing,
    jobId,
    jobReportNumber,
    photoAttachmentId,
    refreshPhotoRecords,
    filename: photoName,
    selectedPhotoRecord,
    setAlteredImageData,
  } = props;
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const [somethingChanged, setSomethingChanged] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [processing, setProcessing] = useState(false);

  let filename = photoName;

  const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');

  if (filename && !filename.includes('_altered')) {
    const extensionIndex = filename.lastIndexOf('.');
    filename = `${filename.substring(0, extensionIndex)}_altered_${timestamp}.png`;
  } else {
    filename = filename.replace(/_altered[^.]*\.png$/, `_altered_${timestamp}.png`);
  }

  const { t } = useTranslation();

  useEffect(() => {
    getImagePath(selectedPhotoRecord, undefined, { fetchPolicy: 'network-only' })
      .then(setImgSrc);
  }, []);

  const addBlackRectangle = () => {
    const { width, height } = editorRef.current.getCanvasSize();
    try {
      editorRef.current.addShape('rect', {
        fill: '#000',
        strokeWidth: 0,
        left: width / 2,
        top: height / 2,
        width: width / 5,
        height: height / 5,
        isRegular: false,
      });
      setSomethingChanged(true);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const addBlurRectangle = () => {
    const { width, height } = editorRef.current.getCanvasSize();
    try {
      editorRef.current.addShape('rect', {
        fill: {
          type: 'filter',
          filter: [{ blur: BLUR }],
        },
        strokeWidth: 0,
        left: width / 2,
        top: height / 2,
        width: width / 5,
        height: height / 5,
        isRegular: false,
      });
      setSomethingChanged(true);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const closeEditor = () => {
    setEnablePhotoEditing(false);
  };

  const requestPreSignedUrlToUpload = async (fileSize) => {
    const extendedName = `${jobReportNumber.toLowerCase()}/${filename}`;
    return putObjectPreSignedUrl(JOB_MOBILE_PHOTO_ENTITY_NAME, extendedName, fileSize);
  };

  function urlToFile(url, mimeType) {
    return (fetch(url)
      .then((res) => {
        return res.arrayBuffer();
      })
      .then((buf) => {
        return new File([buf], filename, { type: mimeType });
      })
    );
  }

  const getAttachmentId = (fileName, entity) => {
    return client
      .mutate({
        mutation: GET_ATTACHMENT_ID,
        variables: {
          entity,
          fileName,
        },
      })
      .then(response => {
        return response.data.persistAttachment.id;
      });
  };

  const saveChanges = async () => {
    try {
      setProcessing(true);
      const dataURL = editorRef.current.toDataURL({ format: 'png' });
      const file = await urlToFile(dataURL, 'image/png');
      const { url: uploadUrl } = await requestPreSignedUrlToUpload(file.size);
      await uploadFileToStorage(uploadUrl, file);
      await setAlteredImageData(
        selectedPhotoRecord.showFindings,
        selectedPhotoRecord.viewInPhotoForm,
        selectedPhotoRecord.order,
        selectedPhotoRecord.id,
        {
          showAltered: true,
          alteredFileName: filename,
          alteredAttachmentId: await getAttachmentId(`${jobReportNumber.toLowerCase()}/${filename}`, JOB_MOBILE_PHOTO_ENTITY_NAME),
        },
      );
      await refreshPhotoRecords();
      closeEditor();
    } catch (err) {
      notification.error('FAILED_TO_EDIT_PHOTO');
      Sentry.captureException(err);
    } finally {
      setProcessing(false);
    }
  };

  const rotateLeft = () => {
    editorRef.current.rotate(-90);
    setSomethingChanged(true);
  };

  const rotateRight = () => {
    editorRef.current.rotate(90);
    setSomethingChanged(true);
  };

  useEffect(() => {
    if (!imgSrc) return;

    if (imgSrc && !editorRef.current && containerRef.current) {
      editorRef.current = new ImageEditor(containerRef.current, {
        includeUI: {
          loadImage: {
            path: imgSrc,
            name: filename,
          },
          menu: ['shape', 'filter'],
          initMenu: 'filter',
          uiSize: {
            width: '100%',
            height: '100%',
          },
          menuBarPosition: 'bottom',
        },
        selectionStyle: {
          cornerSize: 20,
          rotatingPointOffset: 70,
        }
      });
    }
  }, [imgSrc, filename]);

  return (
    <s.Wrapper>
      {imgSrc ? <>
        <div id="editorImage" ref={containerRef} />

        {processing
          ? <Loader text={'Saving..'} />
          : <s.Buttons>
            <Button onClick={() => addBlurRectangle()} type="button">{t('USE_BLUR_TOOL')}</Button>
            <Button onClick={() => addBlackRectangle()} type="button">{t('USE_REDACT_TOOL')}</Button>
            <Button onClick={() => rotateLeft()} type="button">{t('ROTATE_LEFT')}</Button>
            <Button onClick={() => rotateRight()} type="button">{t('ROTATE_RIGHT')}</Button>
            <Button disabled={!somethingChanged} gray={!somethingChanged} onClick={() => saveChanges()}
                    type="button">{t('SAVE')}</Button>
            <Button onClick={closeEditor} type="button">{t('CANCEL')}</Button>
          </s.Buttons>}
      </> : null}
    </s.Wrapper>

  );
};

ImageEditing.propTypes = {
  jobId: PropTypes.number.isRequired,
  jobReportNumber: PropTypes.string.isRequired,
  selectedPhotoRecord: PropTypes.object.isRequired,
  setAlteredImageData: PropTypes.func.isRequired,
  filename: PropTypes.string,
  getImagePath: PropTypes.func,
  photoAttachmentId: PropTypes.number,
  refreshPhotoRecords: PropTypes.func,
  setEnablePhotoEditing: PropTypes.func,
};

ImageEditing.defaultProps = {
  filename: null,
  getImagePath: () => {
  },
  photoAttachmentId: null,
  refreshPhotoRecords: () => {
  },
  setAlteredImageData: () => {
  },
  setEnablePhotoEditing: () => {
  },
};

export default React.memo(ImageEditing);
